<script  module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router, usePage } from "@inertiajs/svelte";
  import { Plus, Edit3, Trash2, FileText, CheckCircle, EyeOff, FileSpreadsheet, ChevronLeft, ChevronRight } from "lucide-svelte";
  import { Button } from "$lib/components/ui/button";
  import { Badge } from "$lib/components/ui/badge/index.js";
  import { toast } from "svelte-sonner";
  import { appStore } from '$lib/stores/appStore';

  let { posts = [], pagination = { current_page: 1, total_pages: 1, total_count: 0, is_first_page: true, is_last_page: true } } = $props();

  const page = usePage();
  const translations = $derived((page.props as any).translations || {});
  const t = $derived(translations.posts || {});

  function deletePost(id: number | string, title: string) {
    const confirmMsg = t.confirm_delete?.replace("%{title}", title) || `Delete post "${title}"?`;
    if (confirm(confirmMsg)) {
      const toastId = toast.loading(t.toast_deleting || "Deleting...");
      router.delete(`/posts/${id}`, {
        onSuccess: () => toast.success(t.toast_delete_success || "Success", { id: toastId }),
        onError: () => toast.error(t.toast_delete_error || "Error", { id: toastId })
      });
    }
  }

  function goToPage(pageNum: number) {
    if (pageNum < 1 || pageNum > pagination.total_pages) return;
    router.get('/posts', { page: pageNum }, { 
      preserveScroll: true,
      only: ['posts', 'pagination']
    });
  }

  // 动态拼装分页展示文本
  const paginationText = $derived(
    t.pagination_info
      ?.replace("%{total}", String(pagination.total_count))
      ?.replace("%{current}", String(pagination.current_page))
      ?.replace("%{total_pages}", String(pagination.total_pages)) || ""
  );


  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<svelte:head>
  <title>{t.title || '文章管理'} | Fentime Aurora</title>
</svelte:head>

<div class="p-8 max-w-6xl mx-auto">
  <header class="flex justify-between items-center mb-8 dark:border-zinc-800 pb-6">
    <div>
      <h1 class="text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 flex items-center gap-2.5">
        <FileText class="size-5 text-zinc-800 dark:text-zinc-200" /> {t.title}
      </h1>
      <p class="text-sm text-muted-foreground mt-1">{t.subtitle}</p>
    </div>
    
    <Button 
      onclick={() => router.get('/posts/new')}
    >
      <Plus class="size-4" /> {t.btn_new}
    </Button>
  </header>

  <div class="bg-card border dark:border-zinc-800 rounded-xl overflow-hidden shadow-sm flex flex-col bg-white dark:bg-zinc-900">
    {#if posts.length === 0}
      <div class="p-16 text-center flex flex-col items-center justify-center max-w-md mx-auto">
        <div class="size-10 bg-zinc-50 dark:bg-zinc-800 border dark:border-zinc-700 rounded-lg flex items-center justify-center mb-4 text-muted-foreground">
          <FileSpreadsheet class="size-5 text-zinc-400" />
        </div>
        <h3 class="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t.empty_title}</h3>
        <p class="text-xs text-muted-foreground mt-1 mb-4">{t.empty_desc}</p>
        <Button size="sm" variant="outline" onclick={() => router.get('/posts/new')} class="text-xs rounded-md">
          {t.empty_action}
        </Button>
      </div>
    {:else}
      <div class="overflow-x-auto">
        <table class="w-full border-collapse text-left text-sm text-muted-foreground">
          <thead class="bg-zinc-50/70 dark:bg-zinc-800/50 text-xs font-semibold text-zinc-700 dark:text-zinc-300 uppercase tracking-wider border-b border-zinc-100 dark:border-zinc-800+">
            <tr>
              <th class="px-6 py-3.5">{t.th_title}</th>
              <th class="px-6 py-3.5">{t.th_category}</th>
              <th class="px-6 py-3.5">{t.th_slug}</th>
              <th class="px-6 py-3.5">{t.th_status}</th>
              <th class="px-6 py-3.5 text-right">{t.th_actions}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-100 dark:divide-zinc-800 font-medium bg-white dark:bg-zinc-900">
            {#each posts as p}
              <tr class="hover:bg-zinc-50/40 dark:hover:bg-zinc-800/40 transition-colors">
                <td class="px-6 py-4 text-zinc-950 dark:text-zinc-50 font-semibold max-w-xs truncate">
                  {p.title}
                </td>
                
                <td class="px-6 py-4">
                  <Badge variant="secondary" class="font-normal px-2.5 py-0.5 bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 rounded-md shadow-none border-none">
                    {p.category_name || t.uncategorized}
                  </Badge>
                </td>
                
                <td class="px-6 py-4 font-mono text-xs text-zinc-400 dark:text-zinc-500">
                  {p.slug}
                </td>
                
                <td class="px-6 py-4">
                  {#if p.published}
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/30 px-2 py-0.5 rounded-md border border-emerald-100 dark:border-emerald-900/50">
                      <CheckCircle class="size-3.5 stroke-[2.5]" /> {t.status_published}
                    </span>
                  {:else}
                    <span class="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-400 dark:text-zinc-500 bg-zinc-50 dark:bg-zinc-950 px-2 py-0.5 rounded-md border border-zinc-200/60 dark:border-zinc-800">
                      <EyeOff class="size-3.5" /> {t.status_draft}
                    </span>
                  {/if}
                </td>
                
                <td class="px-6 py-4 text-right">
                  <div class="flex items-center justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      class="size-8 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-md"
                      onclick={() => router.get(`/posts/${p.id}/edit`)}
                    >
                      <Edit3 class="size-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      class="size-8 text-zinc-400 hover:text-destructive hover:bg-destructive/5 rounded-md"
                      onclick={() => deletePost(p.id, p.title)}
                    >
                      <Trash2 class="size-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between px-6 py-4 border-t dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 text-xs font-medium text-zinc-500">
        <div>
          {@html paginationText}
        </div>
        
        <div class="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            class="h-8 px-3 rounded-md gap-1 bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 shadow-sm"
            disabled={pagination.is_first_page}
            onclick={() => goToPage(pagination.current_page - 1)}
          >
            <ChevronLeft class="size-3.5" /> {t.prev_page}
          </Button>

          <Button
            variant="outline"
            size="sm"
            class="h-8 px-3 rounded-md gap-1 bg-white dark:bg-zinc-950 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 shadow-sm"
            disabled={pagination.is_last_page}
            onclick={() => goToPage(pagination.current_page + 1)}
          >
            {t.next_page} <ChevronRight class="size-3.5" />
          </Button>
        </div>
      </div>
    {/if}
  </div>
</div>